import { SyntheticEvent, useEffect, useState } from "react";

import { sendPasswordSetRequest } from "@/api/auth";
import { Button } from "@/components/ui";
import { AuthCard } from "@/components/ui/AuthCard";
import { FormHeader } from "@/components/ui/FormHeader";
import { FRONT_DOOR_INPUT, Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { TextLink } from "@/components/ui/TextLink";
import { ApiError } from "@/lib/error";

function validatePassword(pass1: string, pass2: string) {
  // FIXME: add proper password strength validation
  return pass1.length > 0 && pass2.length > 0 && pass1 === pass2;
}

export const PasswordSetForm = () => {
  //fields
  const [token, setToken] = useState("");
  const [invitation, setInvitation] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState("");
  // Revealed only once the backend asks for it, so an account without an authenticator never sees
  // a code field, and the form does not have to know up front which kind of account the token is for.
  const [codeRequired, setCodeRequired] = useState(false);
  const [code, setCode] = useState("");

  const [linkSent, setLinkSent] = useState(false);

  function handleSubmit(event: SyntheticEvent) {
    setError("");
    event.preventDefault();
    sendPasswordSetRequest(token, invitation, newPassword, codeRequired ? code : undefined)
      .then(() => setLinkSent(true))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.errorType === "SECOND_FACTOR_REQUIRED") {
          setCodeRequired(true);
          return;
        }
        if (err instanceof ApiError) {
          setError(err.message);
          return;
        }
        throw err;
      });
  }

  function canSubmit() {
    return (
      validatePassword(newPassword, repeatPassword) && (!codeRequired || code.trim().length > 0)
    );
  }

  function hasToken() {
    return token || invitation;
  }

  useEffect(() => {
    const parsedQueryString = Object.fromEntries(new URLSearchParams(window.location.search));
    setToken(parsedQueryString.token as string);
    setInvitation(parsedQueryString.invitation as string);
    setRedirectUrl(parsedQueryString.redirect_url as string);
  }, []);

  return (
    <>
      {!hasToken() && !linkSent && (
        <AuthCard centered>
          <FormHeader
            title="Token parameter not found."
            subtitle="Please copy the whole URL address from the email."
          />
        </AuthCard>
      )}

      {hasToken() && !linkSent && (
        <AuthCard as="form" onSubmit={handleSubmit}>
          <FormHeader
            title="Set a new password"
            subtitle="Type in a new password and you're ready to go!"
          />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-semibold">
                New password
              </Label>
              <Input
                id="newPassword"
                autoFocus
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={FRONT_DOOR_INPUT}
                placeholder="Enter new password"
              />
            </div>

            {error && (
              <div className="text-center text-sm font-semibold text-destructive-fg">{error}</div>
            )}

            <div className="space-y-2">
              <Label htmlFor="repeatPassword" className="text-sm font-semibold">
                Repeat password
              </Label>
              <Input
                id="repeatPassword"
                type="password"
                autoComplete="new-password"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                className={FRONT_DOOR_INPUT}
                placeholder="Repeat new password"
              />
            </div>

            {codeRequired && (
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-semibold">
                  Code from your authenticator app or a backup code
                </Label>
                <Input
                  id="code"
                  autoFocus
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={FRONT_DOOR_INPUT}
                  placeholder="123456"
                />
              </div>
            )}

            <Button
              disabled={!canSubmit()}
              type="submit"
              variant="brand"
              size="xl"
              className="w-full"
            >
              Set password
            </Button>
          </div>
        </AuthCard>
      )}
      {linkSent && (
        <AuthCard centered>
          <FormHeader title="The new password has been set." />
          <div className="pt-4">
            <TextLink href={"/login" + (redirectUrl ? `?redirect_url=${redirectUrl}` : "")}>
              Back to log in
            </TextLink>
          </div>
        </AuthCard>
      )}
    </>
  );
};
